/**
 * ShiftManagement - Cross-module shift scheduling component
 * Works with Dining, Housing, Security staff
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Calendar, Clock, Users, Plus, Edit2, Trash2, 
  CheckCircle, XCircle, RefreshCw, Download, Filter,
  ChevronLeft, ChevronRight, Play, Square, Coffee,
  AlertCircle, ArrowRightLeft
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ShiftManagement = ({ department = null }) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('schedule');
  const [showCreateShift, setShowCreateShift] = useState(false);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedDepartment, setSelectedDepartment] = useState(department || 'all');
  const [viewMode, setViewMode] = useState('day'); // day, week
  
  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Calculate date range for week view
  const getWeekDates = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday start
    const monday = new Date(d.setDate(diff));
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const weekDates = getWeekDates(selectedDate);
  const startDate = viewMode === 'week' ? weekDates[0] : selectedDate;
  const endDate = viewMode === 'week' ? weekDates[6] : selectedDate;

  // Fetch shifts
  const { data: shifts = [], isLoading: shiftsLoading, refetch: refetchShifts } = useQuery({
    queryKey: ['shifts', selectedDepartment, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate
      });
      if (selectedDepartment !== 'all') params.append('department', selectedDepartment);
      
      const res = await fetch(`${API_URL}/api/admin/shifts?${params}`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch shift templates
  const { data: templates = [] } = useQuery({
    queryKey: ['shift-templates', selectedDepartment],
    queryFn: async () => {
      const params = selectedDepartment !== 'all' ? `?department=${selectedDepartment}` : '';
      const res = await fetch(`${API_URL}/api/admin/shifts/templates${params}`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch shift stats
  const { data: stats = {} } = useQuery({
    queryKey: ['shift-stats', selectedDepartment],
    queryFn: async () => {
      const params = selectedDepartment !== 'all' ? `?department=${selectedDepartment}` : '';
      const res = await fetch(`${API_URL}/api/admin/shifts/stats${params}`, { headers });
      if (!res.ok) return {};
      return res.json();
    },
    refetchInterval: 30000
  });

  // Fetch staff for dropdown
  const { data: staffList = [] } = useQuery({
    queryKey: ['all-staff', selectedDepartment],
    queryFn: async () => {
      const allStaff = [];
      
      // Fetch from each department
      if (selectedDepartment === 'all' || selectedDepartment === 'dining') {
        const res = await fetch(`${API_URL}/api/admin/dining/staff`, { headers });
        if (res.ok) {
          const staff = await res.json();
          allStaff.push(...staff.map(s => ({ ...s, department: 'dining' })));
        }
      }
      
      if (selectedDepartment === 'all' || selectedDepartment === 'housing') {
        const res = await fetch(`${API_URL}/api/admin/accommodation/staff`, { headers });
        if (res.ok) {
          const staff = await res.json();
          allStaff.push(...staff.map(s => ({ ...s, department: 'housing' })));
        }
      }
      
      if (selectedDepartment === 'all' || selectedDepartment === 'security') {
        const res = await fetch(`${API_URL}/api/admin/security-officers`, { headers });
        if (res.ok) {
          const staff = await res.json();
          allStaff.push(...staff.map(s => ({ ...s, department: 'security' })));
        }
      }
      
      return allStaff;
    }
  });

  // Create shift mutation
  const createShiftMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/admin/shifts`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create shift');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['shifts']);
      queryClient.invalidateQueries(['shift-stats']);
      setShowCreateShift(false);
      toast.success('Shift created');
    },
    onError: (error) => toast.error(error.message)
  });

  // Create template mutation
  const createTemplateMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/admin/shifts/templates`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create template');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['shift-templates']);
      setShowCreateTemplate(false);
      toast.success('Template created');
    },
    onError: (error) => toast.error(error.message)
  });

  // Clock action mutation
  const clockMutation = useMutation({
    mutationFn: async ({ shiftId, action }) => {
      const res = await fetch(`${API_URL}/api/shifts/${shiftId}/clock`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action })
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Clock action failed');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['shifts']);
      queryClient.invalidateQueries(['shift-stats']);
      toast.success('Clock action recorded');
    },
    onError: (error) => toast.error(error.message)
  });

  // Delete shift mutation
  const deleteShiftMutation = useMutation({
    mutationFn: async (shiftId) => {
      const res = await fetch(`${API_URL}/api/admin/shifts/${shiftId}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) throw new Error('Failed to delete shift');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['shifts']);
      toast.success('Shift deleted');
    },
    onError: (error) => toast.error(error.message)
  });

  // Navigate dates
  const navigateDate = (direction) => {
    const d = new Date(selectedDate);
    if (viewMode === 'week') {
      d.setDate(d.getDate() + (direction * 7));
    } else {
      d.setDate(d.getDate() + direction);
    }
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  // Group shifts by date for week view
  const shiftsByDate = shifts.reduce((acc, shift) => {
    const date = shift.date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(shift);
    return acc;
  }, {});

  const departmentColors = {
    dining: 'bg-orange-100 text-orange-800 border-orange-300',
    housing: 'bg-purple-100 text-purple-800 border-purple-300',
    security: 'bg-blue-100 text-blue-800 border-blue-300',
    general: 'bg-gray-100 text-gray-800 border-gray-300'
  };

  const statusColors = {
    scheduled: 'bg-yellow-100 text-yellow-800',
    in_progress: 'bg-green-100 text-green-800',
    completed: 'bg-blue-100 text-blue-800',
    cancelled: 'bg-red-100 text-red-800'
  };

  return (
    <div className="space-y-6" data-testid="shift-management">
      {/* Header & Stats */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Clock className="h-6 w-6 text-blue-600" />
            Shift Management
          </h2>
          <p className="text-slate-600">Schedule and manage staff shifts across departments</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowCreateTemplate(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Template
          </Button>
          <Button onClick={() => setShowCreateShift(true)} data-testid="create-shift-btn">
            <Plus className="h-4 w-4 mr-2" />
            Create Shift
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Today's Shifts</p>
                <p className="text-2xl font-bold">{stats.today?.total_shifts || 0}</p>
              </div>
              <Calendar className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Scheduled</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.today?.scheduled || 0}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">In Progress</p>
                <p className="text-2xl font-bold text-green-600">{stats.today?.in_progress || 0}</p>
              </div>
              <Play className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Completed</p>
                <p className="text-2xl font-bold text-blue-600">{stats.today?.completed || 0}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Swaps</p>
                <p className="text-2xl font-bold text-purple-600">{stats.pending_swaps || 0}</p>
              </div>
              <ArrowRightLeft className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center justify-between bg-white p-4 rounded-lg border">
        <div className="flex items-center gap-4">
          <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Department" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              <SelectItem value="dining">Dining</SelectItem>
              <SelectItem value="housing">Housing</SelectItem>
              <SelectItem value="security">Security</SelectItem>
            </SelectContent>
          </Select>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setViewMode('day')} 
              className={viewMode === 'day' ? 'bg-blue-50' : ''}>
              Day
            </Button>
            <Button variant="outline" size="sm" onClick={() => setViewMode('week')}
              className={viewMode === 'week' ? 'bg-blue-50' : ''}>
              Week
            </Button>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigateDate(-1)}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <Input 
            type="date" 
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-40"
          />
          <Button variant="ghost" size="icon" onClick={() => navigateDate(1)}>
            <ChevronRight className="h-5 w-5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}>
            Today
          </Button>
          <Button variant="ghost" size="icon" onClick={() => refetchShifts()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="schedule">Schedule</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="payroll">Payroll Report</TabsTrigger>
        </TabsList>

        {/* Schedule Tab */}
        <TabsContent value="schedule" className="mt-4">
          {shiftsLoading ? (
            <div className="text-center py-8 text-gray-500">Loading shifts...</div>
          ) : viewMode === 'week' ? (
            // Week View
            <div className="grid grid-cols-7 gap-2">
              {weekDates.map((date) => (
                <div key={date} className="min-h-[300px]">
                  <div className={`text-center p-2 rounded-t-lg font-medium ${
                    date === new Date().toISOString().split('T')[0] 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-100'
                  }`}>
                    {new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </div>
                  <div className="border rounded-b-lg p-2 space-y-2 min-h-[250px] bg-gray-50">
                    {(shiftsByDate[date] || []).map((shift) => (
                      <div 
                        key={shift.id}
                        className={`p-2 rounded text-xs border ${departmentColors[shift.department] || departmentColors.general}`}
                      >
                        <p className="font-medium truncate">{shift.staff_name}</p>
                        <p className="text-gray-600">{shift.start_time} - {shift.end_time}</p>
                        <Badge className={`text-xs mt-1 ${statusColors[shift.status]}`}>
                          {shift.status}
                        </Badge>
                      </div>
                    ))}
                    {(!shiftsByDate[date] || shiftsByDate[date].length === 0) && (
                      <p className="text-center text-gray-400 text-xs py-4">No shifts</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Day View - Detailed list
            <div className="space-y-3">
              {shifts.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500">No shifts scheduled for this day</p>
                  <Button className="mt-4" onClick={() => setShowCreateShift(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Shift
                  </Button>
                </div>
              ) : (
                shifts.map((shift) => (
                  <Card key={shift.id} className="hover:shadow-md transition-shadow" data-testid={`shift-${shift.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                            shift.department === 'dining' ? 'bg-orange-100' :
                            shift.department === 'housing' ? 'bg-purple-100' :
                            shift.department === 'security' ? 'bg-blue-100' : 'bg-gray-100'
                          }`}>
                            <Users className={`h-6 w-6 ${
                              shift.department === 'dining' ? 'text-orange-600' :
                              shift.department === 'housing' ? 'text-purple-600' :
                              shift.department === 'security' ? 'text-blue-600' : 'text-gray-600'
                            }`} />
                          </div>
                          <div>
                            <p className="font-semibold">{shift.staff_name}</p>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Badge variant="outline" className="capitalize">{shift.department}</Badge>
                              {shift.location_name && <span>• {shift.location_name}</span>}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-medium">{shift.start_time} - {shift.end_time}</p>
                            <p className="text-sm text-gray-500">{shift.scheduled_hours}h scheduled</p>
                          </div>
                          
                          <Badge className={statusColors[shift.status]}>
                            {shift.status?.replace('_', ' ')}
                          </Badge>
                          
                          {/* Clock Actions */}
                          <div className="flex items-center gap-1">
                            {shift.status === 'scheduled' && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-green-600 hover:bg-green-50"
                                onClick={() => clockMutation.mutate({ shiftId: shift.id, action: 'clock_in' })}
                              >
                                <Play className="h-4 w-4 mr-1" />
                                Clock In
                              </Button>
                            )}
                            {shift.status === 'in_progress' && (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => clockMutation.mutate({ shiftId: shift.id, action: 'break_start' })}
                                >
                                  <Coffee className="h-4 w-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-red-600 hover:bg-red-50"
                                  onClick={() => clockMutation.mutate({ shiftId: shift.id, action: 'clock_out' })}
                                >
                                  <Square className="h-4 w-4 mr-1" />
                                  Clock Out
                                </Button>
                              </>
                            )}
                            {shift.status === 'scheduled' && (
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => deleteShiftMutation.mutate(shift.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Additional Info */}
                      {(shift.clock_in_time || shift.actual_hours) && (
                        <div className="mt-3 pt-3 border-t flex items-center gap-6 text-sm">
                          {shift.clock_in_time && (
                            <span className="text-green-600">
                              Clocked in: {new Date(shift.clock_in_time).toLocaleTimeString()}
                            </span>
                          )}
                          {shift.clock_out_time && (
                            <span className="text-red-600">
                              Clocked out: {new Date(shift.clock_out_time).toLocaleTimeString()}
                            </span>
                          )}
                          {shift.actual_hours && (
                            <span className="font-medium">
                              Actual: {shift.actual_hours}h
                              {shift.overtime_hours > 0 && (
                                <span className="text-orange-600 ml-1">(+{shift.overtime_hours}h OT)</span>
                              )}
                            </span>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {templates.map((template) => (
              <Card key={template.id} data-testid={`template-${template.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <div 
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: template.color }}
                      />
                      {template.name}
                    </CardTitle>
                    <Badge variant="outline" className="capitalize">{template.department}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Time</span>
                      <span className="font-medium">{template.start_time} - {template.end_time}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Duration</span>
                      <span className="font-medium">{template.duration_hours}h</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Break</span>
                      <span className="font-medium">{template.break_duration_minutes} min</span>
                    </div>
                    {template.description && (
                      <p className="text-gray-500 text-xs mt-2">{template.description}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {templates.length === 0 && (
              <div className="col-span-3 text-center py-12 bg-gray-50 rounded-lg">
                <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">No shift templates created yet</p>
                <Button className="mt-4" onClick={() => setShowCreateTemplate(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Template
                </Button>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Payroll Tab */}
        <TabsContent value="payroll" className="mt-4">
          <PayrollReport headers={headers} />
        </TabsContent>
      </Tabs>

      {/* Create Shift Dialog */}
      <Dialog open={showCreateShift} onOpenChange={setShowCreateShift}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Shift</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const form = new FormData(e.target);
              createShiftMutation.mutate({
                staff_id: form.get('staff_id'),
                department: form.get('department'),
                template_id: form.get('template_id') === 'none' ? null : form.get('template_id'),
                date: form.get('date'),
                start_time: form.get('start_time'),
                end_time: form.get('end_time'),
                location_name: form.get('location_name'),
                notes: form.get('notes')
              });
            }}
            className="space-y-4"
          >
            <div>
              <label className="text-sm font-medium">Department *</label>
              <Select name="department" defaultValue={selectedDepartment !== 'all' ? selectedDepartment : 'dining'}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dining">Dining</SelectItem>
                  <SelectItem value="housing">Housing</SelectItem>
                  <SelectItem value="security">Security</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium">Staff Member *</label>
              <Select name="staff_id" required>
                <SelectTrigger>
                  <SelectValue placeholder="Select staff" />
                </SelectTrigger>
                <SelectContent>
                  {staffList.map((staff) => (
                    <SelectItem key={staff.id} value={staff.id}>
                      {staff.name} ({staff.department})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium">Use Template (Optional)</label>
              <Select name="template_id">
                <SelectTrigger>
                  <SelectValue placeholder="No template" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No template</SelectItem>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} ({t.start_time}-{t.end_time})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium">Date *</label>
              <Input name="date" type="date" defaultValue={selectedDate} required />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Start Time *</label>
                <Input name="start_time" type="time" defaultValue="09:00" required />
              </div>
              <div>
                <label className="text-sm font-medium">End Time *</label>
                <Input name="end_time" type="time" defaultValue="17:00" required />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium">Location</label>
              <Input name="location_name" placeholder="e.g., Main Kitchen, Building A" />
            </div>
            
            <div>
              <label className="text-sm font-medium">Notes</label>
              <Textarea name="notes" placeholder="Any special instructions..." rows={2} />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateShift(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createShiftMutation.isPending}>
                {createShiftMutation.isPending ? 'Creating...' : 'Create Shift'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Template Dialog */}
      <Dialog open={showCreateTemplate} onOpenChange={setShowCreateTemplate}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Shift Template</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const form = new FormData(e.target);
              createTemplateMutation.mutate({
                name: form.get('name'),
                department: form.get('department'),
                start_time: form.get('start_time'),
                end_time: form.get('end_time'),
                break_duration_minutes: parseInt(form.get('break_duration')) || 30,
                color: form.get('color'),
                description: form.get('description')
              });
            }}
            className="space-y-4"
          >
            <div>
              <label className="text-sm font-medium">Template Name *</label>
              <Input name="name" placeholder="e.g., Morning Shift" required />
            </div>
            
            <div>
              <label className="text-sm font-medium">Department *</label>
              <Select name="department" defaultValue="dining">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dining">Dining</SelectItem>
                  <SelectItem value="housing">Housing</SelectItem>
                  <SelectItem value="security">Security</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Start Time *</label>
                <Input name="start_time" type="time" defaultValue="09:00" required />
              </div>
              <div>
                <label className="text-sm font-medium">End Time *</label>
                <Input name="end_time" type="time" defaultValue="17:00" required />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Break Duration (min)</label>
                <Input name="break_duration" type="number" defaultValue={30} />
              </div>
              <div>
                <label className="text-sm font-medium">Color</label>
                <Input name="color" type="color" defaultValue="#3B82F6" className="h-10" />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea name="description" placeholder="Template description..." rows={2} />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateTemplate(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createTemplateMutation.isPending}>
                {createTemplateMutation.isPending ? 'Creating...' : 'Create Template'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Payroll Report Sub-component
const PayrollReport = ({ headers }) => {
  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [department, setDepartment] = useState('all');

  const { data: payroll, isLoading, refetch } = useQuery({
    queryKey: ['payroll-report', startDate, endDate, department],
    queryFn: async () => {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      if (department !== 'all') params.append('department', department);
      
      const res = await fetch(`${API_URL}/api/admin/shifts/payroll?${params}`, { headers });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: false
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 bg-white p-4 rounded-lg border">
        <div>
          <label className="text-sm font-medium">Start Date</label>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div>
          <label className="text-sm font-medium">End Date</label>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
        <div>
          <label className="text-sm font-medium">Department</label>
          <Select value={department} onValueChange={setDepartment}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="dining">Dining</SelectItem>
              <SelectItem value="housing">Housing</SelectItem>
              <SelectItem value="security">Security</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => refetch()} className="mt-5">
          Generate Report
        </Button>
      </div>

      {isLoading && <div className="text-center py-8">Loading...</div>}

      {payroll && (
        <div className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Total Staff</p>
                <p className="text-2xl font-bold">{payroll.totals.total_staff}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Total Shifts</p>
                <p className="text-2xl font-bold">{payroll.totals.total_shifts}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Actual Hours</p>
                <p className="text-2xl font-bold text-green-600">{payroll.totals.total_actual_hours}h</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Overtime</p>
                <p className="text-2xl font-bold text-orange-600">{payroll.totals.total_overtime_hours}h</p>
              </CardContent>
            </Card>
          </div>

          {/* Staff Table */}
          <Card>
            <CardHeader>
              <CardTitle>Staff Hours Report</CardTitle>
            </CardHeader>
            <CardContent>
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Staff</th>
                    <th className="text-left py-2">Department</th>
                    <th className="text-right py-2">Shifts</th>
                    <th className="text-right py-2">Scheduled</th>
                    <th className="text-right py-2">Actual</th>
                    <th className="text-right py-2">Overtime</th>
                  </tr>
                </thead>
                <tbody>
                  {payroll.staff_reports.map((staff) => (
                    <tr key={staff.staff_id} className="border-b">
                      <td className="py-2 font-medium">{staff.staff_name}</td>
                      <td className="py-2 capitalize">{staff.department}</td>
                      <td className="py-2 text-right">{staff.shifts_count}</td>
                      <td className="py-2 text-right">{staff.total_scheduled_hours}h</td>
                      <td className="py-2 text-right text-green-600">{staff.total_actual_hours}h</td>
                      <td className="py-2 text-right text-orange-600">{staff.total_overtime_hours}h</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      )}

      {!payroll && !isLoading && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Download className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">Select date range and click "Generate Report"</p>
        </div>
      )}
    </div>
  );
};

export default ShiftManagement;
